import {
  EntitiesUnifiedData,
  useMutationEntities,
} from "@apis:entities/entities";
import {
  SummarizationUnifiedData,
  useMutationSummarization,
} from "@apis:summarization/summarization";
import { Header } from "@components:Header";
import { SVGInternalIcon } from "@components:SVGInternalIcon";
import { SwitchPage } from "@components:SwitchPage";
import { useBotSelection } from "@hooks:useBotSelection";
import { BotSelectionPersistent } from "@modules:BotSelection/BotSelection";
import { Box, Button, Typography } from "@mui/material";
import { useToasterStoreActions } from "@stores:global";
import { useState } from "react";

import { TextSummaryResult } from "./Components/TextSummaryResult";
import { UploadButton } from "./Components/UploadButton";
import { URLButton } from "./Components/URLButton";
import { WriteButton } from "./Components/WriteButton";
import { DocsSource } from "./types";

export const Docs = () => {
  const { getSelectedBot } = useBotSelection();
  const toastActions = useToasterStoreActions();

  const selectedBot = getSelectedBot();

  const [textSource, setTextSource] = useState<string>("");
  const [fileSource, setFileSource] = useState<File | undefined>(undefined);
  const [urlSource, setUrlSource] = useState<string>("");
  // Summary
  const [summaryResult, setSummaryResult] = useState<string | undefined>(
    undefined,
  );
  const [waitingSummaryResult, setWaitingSummaryResult] =
    useState<boolean>(false);
  // Entities and relationships
  const [EARResult, setEARResult] = useState<string | undefined>(undefined);
  const [waitingEARResult, setWaitingEARResult] = useState<boolean>(false);

  const summaryAIMutation = useMutationSummarization({
    onSuccess: (data) => {
      setSummaryResult(data);
      setTextSource("");
      setUrlSource("");
      setFileSource(undefined);
    },
    onMutate: ({ text, file }: DocsSource) => {
      setWaitingSummaryResult(true);
      return { text, file };
    },
    onSettled: () => {
      setWaitingSummaryResult(false);
    },
    onError: () => {
      setSummaryResult(undefined);
    },
  });

  const getEntitiesRelationshipMutation = useMutationEntities({
    onSuccess: (data) => {
      setEARResult(data);
    },
    onMutate: ({ text, file }: DocsSource) => {
      setWaitingEARResult(true);
      return { text, file };
    },
    onSettled: () => {
      setWaitingEARResult(false);
    },
    onError: () => {
      setEARResult(undefined);
    },
  });

  const isNemoSummaryReady =
    (!!textSource || !!fileSource || !!urlSource) && selectedBot;
  const isShownTextSummary = !!summaryResult || waitingSummaryResult;
  const isShownEntitiesRelationship = !!EARResult || waitingEARResult;

  const handleAIDocs = async (source: DocsSource) => {
    // Determine which type of source we're dealing with (file, text, or url)
    let type: "file" | "text" | "url";
    if (source.file) {
      type = "file";
    } else if (source.url) {
      type = "url";
    } else {
      type = "text";
    }

    // Create the properly formatted data for both API calls
    const summarizationData = {
      type,
      botId: source.botId,
      ...(type === "file" && { file: source.file }),
      ...(type === "text" && { text: source.text }),
      ...(type === "url" && { url: source.url }),
    } as SummarizationUnifiedData;

    const entitiesData = {
      type,
      botId: source.botId,
      nEntities: source.nEntities,
      ...(type === "file" && { file: source.file }),
      ...(type === "text" && { text: source.text }),
      ...(type === "url" && { url: source.url }),
    } as EntitiesUnifiedData;

    await toastActions.setToasterPromise(
      Promise.allSettled([
        summaryAIMutation.mutateAsync(summarizationData),
        getEntitiesRelationshipMutation.mutateAsync(entitiesData),
      ]),
      {
        onPending: () => ({ message: "AI is summarizing ..." }),
        onSuccess: () => ({ message: "AI generated a comprehensive summary" }),
        onError: () => ({ message: "AI failed to summarize" }),
      },
    );
  };

  return (
    <Box
      sx={{
        background: (theme) => theme.palette.background.default,
        minHeight: "100vh",
        padding: "12px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "30px",
          background: (theme) => theme.palette.common.white,
          flexGrow: 1,
        }}
      >
        <Header
          sx={{
            background: (theme) => theme.palette.common.white,
            borderTopLeftRadius: "30px",
            borderTopRightRadius: "30px",
          }}
          rightSide={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box sx={{ paddingX: "0.5rem" }}>
                <BotSelectionPersistent />
              </Box>
              <SwitchPage />
            </Box>
          }
          leftSide={
            <Box
              sx={{
                marginLeft: "1.5rem",
              }}
            >
              <SVGInternalIcon
                icon="nemoIcon"
                sx={{
                  width: "120px",
                  height: "120px",
                }}
              />
            </Box>
          }
        />
        <Box
          sx={{
            flex: "1 1 0% !important",
            textAlign: "center",
            paddingTop: "8%",
          }}
        >
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              fontWeight: "bold",
              fontSize: "32px",
              lineHeight: "32px",
            }}
          >
            Nemo Docs
          </Typography>
          <Typography
            sx={{
              color: (theme) => theme.palette.text.primary,
              marginTop: "10px",
              fontSize: "24px",
              lineHeight: "32px",
              fontWeight: "500",
            }}
          >
            Hey there, I am Nemo Docs! Upload your file here or simply write any
            text to explore your data with AI
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              marginTop: "3rem",
              flexWrap: "wrap",
            }}
          >
            <WriteButton
              content={textSource ?? ""}
              onContentChange={(e) => {
                setFileSource(undefined);
                setUrlSource("");
                setTextSource(e.target.value);
              }}
            />
            <UploadButton
              file={fileSource}
              onFileChange={(e) => {
                setTextSource("");
                setUrlSource("");
                setFileSource(e.target.files?.[0]);
              }}
            />
            <URLButton
              content={urlSource}
              onContentChange={(e) => {
                setTextSource("");
                setFileSource(undefined);
                setUrlSource(e.target.value);
              }}
            />
          </Box>
          <Box
            sx={{
              marginTop: "2rem",
            }}
          >
            <Button
              variant="contained"
              disabled={!isNemoSummaryReady}
              onClick={async () => {
                await handleAIDocs({
                  text: textSource,
                  file: fileSource,
                  botId: selectedBot?.id as string,
                  url: urlSource,
                });
              }}
              sx={{
                backgroundColor: "#1FADB2",
                color: "white",
                fontWeight: "500",
                "&:hover": {
                  backgroundColor: "#147275",
                },
              }}
            >
              Ask Nemo
            </Button>
          </Box>
          {(isShownTextSummary || isShownEntitiesRelationship) && (
            <TextSummaryResult
              textSummary={summaryResult ?? ""}
              textEAR={EARResult ?? ""}
              inPulseSummary={waitingSummaryResult}
              inPulseEAR={waitingEARResult}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
